import { prepareToast } from './toast';
import { ErrorTier, type NormalizedError } from '@/errors';
import { useAppStore } from '@/stores/app';
import { useErrorStore } from '@/stores/error';

/**
 * What a view should be showing, derived from the flags below.
 *
 *  - `empty`      — nothing has been asked for yet.
 *  - `first-load` — in flight, and there has never been a result: there is nothing to keep on screen.
 *  - `reloading`  — in flight, and a previous result is still on screen: keep it, dim it, show progress.
 *  - `ready`      — a result landed.
 *  - `failed`     — the last attempt failed.
 */
export type LoadingPhase = 'empty' | 'first-load' | 'reloading' | 'ready' | 'failed';

/**
 * True while a request is in flight, whichever kind. Drives `aria-busy` and the progress line.
 *
 * Here beside the type it takes, rather than in the perfusion section module that first needed it.
 * It knows nothing about a section — the summary loader already asks it — so leaving it there made
 * a caller import from `perfusionSection` for a phase that has nothing to do with one.
 *
 * A function over the phase and not an `isBusy` getter on the class below, because half the callers
 * have no instance to ask: a section's phase is `resolveContentPhase`'s return value, not a
 * loader's own.
 */
export const isBusyPhase = (phase: LoadingPhase) =>
  phase === 'first-load' || phase === 'reloading';

/**
 * What a region should be showing, from its request's phase and how much it has on screen.
 *
 * `LoadingPhase` describes the request; a region also has to answer "is there anything to keep".
 * The two readings of `empty` — "nothing was asked for" and "the answer was nothing" — are one
 * state for a view: nothing to show and nothing in flight. And `reloading` promises values to keep,
 * so with a count of zero it is a first load, because dimming an empty page shows the reader
 * nothing where a skeleton shows them the shape of the answer.
 *
 * The mirror of that rule holds too: values on screen with a request in flight are being reloaded,
 * whether or not the loader in flight is the one that put them there. `first-load` is a property of
 * a `LoadingStateModel` — "this loader has not finished before" — and that is a different question
 * from "the region is empty" wherever two reads fill one region. The interactive dashboard's
 * summary card is exactly that: the combined interactive response fills it, and so does the matrix
 * route's separate summary read, so a switch between the two views of a case arrives with the card
 * full and this loader at `first-load`. Rendering that as a first load draws a skeleton on top of
 * readable values.
 *
 * `failed` passes through whatever the count, on purpose. A region that has values and a failed
 * refresh renders the failure *above* them rather than instead of them: dropping the values a
 * request failed to replace is the blanking this whole seam exists to remove, and it would arrive
 * at the moment the last good reading matters most.
 *
 * What the count counts is the caller's business and worth stating at each call site: rows in a
 * perfusion section's table, fields across every graph group on the interactive route.
 */
export const resolveContentPhase = (loadingPhase: LoadingPhase, count: number): LoadingPhase => {
  if (loadingPhase === 'reloading' && count === 0) {
    return 'first-load';
  }
  if (loadingPhase === 'first-load' && count > 0) {
    return 'reloading';
  }
  if (loadingPhase === 'ready' && count === 0) {
    return 'empty';
  }
  return loadingPhase;
};

/**
 * Async state for one operation: loading / finished / failed, plus the error that caused it.
 *
 * This class used to do five jobs — async state, toast presentation, Sentry reporting, HTTP semantics
 * and 401 navigation — from a per-request object instantiated ~70 times. There was nowhere to put
 * policy like "rate-limit error toasts" or "don't report expected errors", because policy is a
 * single-instance concern. Those four jobs now live in `src/errors/` and `src/stores/error.ts`.
 *
 * The public surface is unchanged on purpose, so all ~97 existing `setError` call sites and both the
 * `ref(...)` and `reactive(...)` wrappings keep working untouched.
 *
 * Which to use:
 *  - `setError(e)`   — "this async operation failed, use the default presentation".
 *  - `errorStore.handle(e, { ... })` — anything else: a different tier, a retry, a fatal screen.
 */
export class LoadingStateModel {
  isLoading: boolean = false;
  isFinished: boolean = false;
  isFailed: boolean = false;
  message: string = '';
  options: object = {};

  /** Set by `setError`. Read it to render `<ErrorState>` instead of relying on a toast. */
  error?: NormalizedError;

  /**
   * The five states a view actually has to render, in one place.
   *
   * The flags below already carried this: `isFinished` latches on the first `stopLoadingAndFinish()`
   * and survives `startLoading()`, so `isLoading && isFinished` is a reload with usable values still
   * on screen. Nothing read it — all 48 `ProgressSpinner` sites keyed on `isLoading` alone and blanked
   * their content on every refresh. Switch on `phase` rather than re-deriving the rule at the call site.
   *
   * `failed` is checked before `ready` because a failed request sets both: the `finally` that runs
   * `stopLoadingAndFinish()` does not know the `catch` fired.
   */
  get phase(): LoadingPhase {
    if (this.isLoading) {
      return this.isFinished ? 'reloading' : 'first-load';
    }
    if (this.isFailed) {
      return 'failed';
    }
    return this.isFinished ? 'ready' : 'empty';
  }

  constructor({
    isLoading = false, isFinished = false, isFailed = false, msg = '', options = {}
  }) {
    this.isLoading = isLoading;
    this.isFinished = isFinished;
    this.isFailed = isFailed;
    this.message = msg;
    this.options = options;

    if (Object.keys(options).length > 0) {
      this.setOptions(options);
    }
  }
  setState({
    isLoading = false, isFinished = false, isFailed = false, msg = '', options = {}
  }) {
    this.isLoading = isLoading;
    this.isFinished = isFinished;
    this.isFailed = isFailed;
    this.message = msg;
    // Guarded on the parameter, not `this.options`. The old check read the instance field while the
    // constructor read the argument, so `setState({ options })` silently dropped them on every loader
    // that was constructed without options — which is all of them.
    if (Object.keys(options || {}).length > 0) {
      this.setOptions(options);
    }
  }
  setOptions(options: object) {
    this.options = { ...this.options, ...options };
  }

  /**
   * Record a failure and hand it to the error store, which classifies, reports and presents it.
   *
   * Returns the normalized error so a caller that wants an inline error state can use it directly.
   */
  setError(error: unknown): NormalizedError {
    this.isFailed = true;
    // The source is deliberately not forced to HTTP. A request that failed already carries the
    // interceptor's classification on the error itself, so this hint was ignored for it — and the
    // only errors it *did* reach are the non-HTTP ones (a GeolocationPositionError, the fabricated
    // `Locked` literals, a bare string), which it then mislabelled as http in every report.
    const normalized = useErrorStore().handle(error);
    this.error = normalized;
    this.message = normalized.serverMessage ?? this.message;
    return normalized;
  }

  /**
   * Fail the whole view rather than a corner of it: replaces the router outlet with an error screen.
   * For an initial load the user cannot work around — a failed study fetch, say.
   */
  setFatalError(error: unknown, retry?: () => void | Promise<void>): NormalizedError {
    this.isFailed = true;
    const normalized = useErrorStore().handle(error, {
      tier: ErrorTier.FATAL,
      retry
    });
    this.error = normalized;
    return normalized;
  }

  setSuccess(message: string, title: string = 'Success!') {
    useAppStore().addToast(prepareToast('success', title, message));
  }
  startLoading() {
    this.isLoading = true;
    // Clear the previous failure, or an inline error state latches forever: `isFailed` was never
    // reset, which is why the only two inline error states in the app never recovered on retry.
    this.isFailed = false;
    this.error = undefined;
  }
  stopLoadingAndFinish() {
    this.isLoading = false;
    this.isFinished = true;
  }

  /** Back to a clean slate, for a panel that unmounts and remounts. */
  reset() {
    this.isLoading = false;
    this.isFinished = false;
    this.isFailed = false;
    this.message = '';
    this.error = undefined;
  }
}
